import { Aggregation } from '../../../shared/interfaces/visualizations/aggregation.interface'
import { Component, Input, OnInit } from '@angular/core'
import { CountryFilterInterface } from './interfaces/country-filter.interface'
import { SmartLinkService } from '../../../shared/services/smart-link.service'
import { SmartLink } from '../../../shared/interfaces/smart-link.interface'
import { IncentiveFamilyService } from '../../../shared/services/incentive-family.service'
import * as fileSaver from 'file-saver'

@Component({
  selector: 'app-aggregation-table',
  templateUrl: './aggregation-table.component.html',
  styleUrls: ['./aggregation-table.component.scss'],
})
export class AggregationTableComponent implements OnInit {
  @Input() aggregation!: Aggregation;
  @Input() selectedIncentiveType: string = 'Both';
  @Input() selectedCountries: string[] = [];

  countryOptions: CountryFilterInterface[];
  rows: any[] = [];
  filteredRows: any[] = [];

  incentiveTypes = [
    'Both',
    'Income-based',
    'Expenditure-based'
  ];
  countryGroupOptions: CountryGroupOption[] = []

  constructor(private smartLinkService: SmartLinkService, private incentiveFamilyService: IncentiveFamilyService) {}

  ngOnInit(): void {
    this.rows = this.aggregation.datasets ?? [];

    this.countryOptions = [
      ...new Map(
        this.rows.map(row => [
          row.country?.iso,
          {
            iso: row.country?.iso,
            label: row.country?.label
          }
        ])
      ).values()
    ].sort((a, b) => a.label.localeCompare(b.label));

    const countriesMap = new Map<string, CountryOption>();

    this.rows.forEach(row => {
      const country = row.country;

      if (country?.iso && !countriesMap.has(country.iso)) {
        countriesMap.set(country.iso, {
          iso: country.iso,
          label: country.label
        });
      }
    });

    const countryGroups: CountryGroup[] =
      this.rows[0]?.countryGroups ?? [];

    const groupedCountryIsos = new Set(
      countryGroups.flatMap(group => group.isoCodes)
    );

    this.countryGroupOptions = countryGroups.map(group => ({
      group: group.group,
      label: group.label,
      countries: group.isoCodes
        .map(iso => countriesMap.get(iso))
        .filter((country): country is CountryOption => !!country)
        .sort((a, b) => a.label.localeCompare(b.label))
    }));

    const ungroupedCountries = [...countriesMap.values()]
      .filter(country => !groupedCountryIsos.has(country.iso))
      .sort((a, b) => a.label.localeCompare(b.label));

    if (ungroupedCountries.length) {
      this.countryGroupOptions.push({
        group: 'other',
        label: 'Non-OECD and non-EU countries',
        countries: ungroupedCountries
      });
    }
    this.applyFilters();
  }

  applyFilters(): void {

    this.filteredRows = this.rows.filter(row => {

      const countryMatch = row.country != undefined &&
        (this.selectedCountries.length === 0 ||
        this.selectedCountries.includes(row.country?.iso));

      const incentiveMatch =
        this.selectedIncentiveType === 'Both' ||
        row.incentiveType === this.selectedIncentiveType;

      return countryMatch && incentiveMatch;
    });
  }

  getValue(row: any, column: string) {

    switch(column) {

      case 'Country':
        return row.country?.label;

      case 'Type of incentive':
        return row.incentiveType;

      case 'Incentive Name':
        return row.incentiveName;

      case 'Instrument Type':
        return row.instrumentType;

      case 'Year of introduction':
        return row.introductionYear;

      case 'Enforcing legislation':
        return row.enforcingLegislation;

      default:
        return '';
    }
  }

  countryDropdownOpened = false;

  toggleCountryDropdown() {
    this.countryDropdownOpened = !this.countryDropdownOpened;
  }


  toggleCountry(iso: string, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;

    if (checked) {
      this.selectedCountries.push(iso);
    } else {
      this.selectedCountries =
        this.selectedCountries.filter(c => c !== iso);
    }

    this.applyFilters();
  }

  toggleCountryGroup(group: CountryGroupOption): void {
    const countryIsos = group.countries.map(country => country.iso);

    const allSelected = countryIsos.every(
      iso => this.selectedCountries.includes(iso)
    );

    if (allSelected) {
      this.selectedCountries = this.selectedCountries.filter(
        iso => !countryIsos.includes(iso)
      );
    } else {
      this.selectedCountries = [
        ...new Set([
          ...this.selectedCountries,
          ...countryIsos
        ])
      ];
    }

    this.applyFilters();
  }

  isCountryGroupSelected(group: CountryGroupOption): boolean {
    return group.countries.length > 0 &&
      group.countries.every(
        country => this.selectedCountries.includes(country.iso)
      );
  }

  isCountryGroupIndeterminate(group: CountryGroupOption): boolean {
    const selectedCount = group.countries.filter(
      country => this.selectedCountries.includes(country.iso)
    ).length;

    return selectedCount > 0 &&
      selectedCount < group.countries.length;
  }

  getCountryLabel(iso: string): string {
    return this.countryOptions.find(
      c => c.iso === iso
    )?.label ?? iso;
  }

  clearCountries(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedCountries = [];
    this.applyFilters();
  }

  isClickableColumn(column: string): boolean {
    return column === 'Country' || column === 'Incentive Name'
  }

  onCellClick(row: any, column: string): void {
    if (row.isDisabled) {
      return
    }

    if (column === 'Country') {
      let page = this.incentiveFamilyService.getPageByType(row.incentiveType)
      if (!row.pages?.includes(page)) {
        return
      }
      this.incentiveFamilyService.setFamilyByType(row.incentiveType)
      const link: SmartLink = {
        view: 'countryPage',
        params: [
          {
            name: 'countryId',
            value: this.getValue(row, column)
          }
        ]
      }
      this.smartLinkService.goToLink(link)
      return
    }

    if (column === 'Incentive Name' && row.incentiveLink) {
      this.incentiveFamilyService.setFamilyByType(row.incentiveType)
      const link: SmartLink = {
        view: 'incentiveDetails',
        params: [
          {
            name: 'incentiveId',
            value: row.incentiveLink.split('/').pop() ?? ''
          }
        ]
      }
      this.smartLinkService.goToLink(link)
    }
  }

  downloadDataset(aggregation: Aggregation): void {
    const blob = new Blob([aggregation.csv], {
      type: 'text/csv;charset=utf-8;'
    })
    fileSaver.saveAs(blob, `${this.aggregation.label}.csv`)
  }
}

interface CountryOption {
  iso: string;
  label: string;
}

interface CountryGroup {
  group: string;
  label: string;
  isoCodes: string[];
}

interface CountryGroupOption {
  group: string;
  label: string;
  countries: CountryOption[];
}
